import classNames from 'classnames';
import dayjs from 'dayjs';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { InputSwitch } from 'primereact/inputswitch';

import { ColumnDescription } from '@/components/common/ui/table/ColumnDescription';
import { giftStatus } from '@/shared/constants/gift';
import { GiftResponseType } from '@/types/gift';
import { Pagination } from '@/types/pagination';

export const GiftColumns = (
  t: any,
  onEditGift?: (id: number) => void,
  onDeleteGift?: (rowData: GiftResponseType) => void,
  onEditStatusGift?: (isActive: boolean, rowData: GiftResponseType) => void,
  pagination?: Pagination,
): ColumnDescription[] => [
  {
    dataField: 'index',
    text:  t('gift_list.id'),
    headerClassname: 'text-center min-w-[80px]',
    classname: 'min-w-[80px] text-gray-700 text-center',
    formatter: (rowData, index) => {
      const pageOffset = ((pagination?.paginate ?? 1) - 1) * 10;
      return index.rowIndex + pageOffset + 1; 
    },
  },
  {
    dataField: 'name',
    text: t('gift_list.name'),
    headerClassname: 'min-w-[300px]',
    classname: 'min-w-[300px]',
    formatter: (rowData) => {
      return (
        <div>
          <Tooltip target={`.tooltip-point-${rowData.id}`}>
            {rowData.name}
          </Tooltip>
          <div
            className={classNames(
              'line-clamp-2 text-gray-700 break-all',
              `tooltip-point-${rowData.id}`,
            )}
            data-pr-position="top"
          >
            {rowData.name}
          </div>
        </div>
      );
    },
  },
  {
    dataField: 'gift_type',
    text: t('gift_list.gift_type'),
    headerClassname: 'min-w-[150px]',
    classname: 'min-w-[150px] text-gray-700',
    formatter: (rowData) => {
      return (
        <div>
          <Tooltip target={`.tooltip-type-${rowData.gift_types?.id || ''}`}>
            {rowData.gift_types?.name || ''}
          </Tooltip>
          <div
            className={classNames(
              'line-clamp-1 text-gray-700 break-all',
              `tooltip-type-${rowData.gift_types?.id || ''}`,
            )}
            data-pr-position="top"
          >
            {rowData.gift_types?.name || '-'}
          </div>
        </div>
      );
    },
  },
  {
    dataField: 'start_date',
    text: t('gift_list.start_date'),
    headerClassname: 'w-[120px]',
    classname: 'w-[120px] text-gray-700',
    sort: true,
    formatter: (rowData) => (
      <div>
        {rowData.start_date
          ? dayjs(rowData.start_date.toString()).format('YYYY/MM/DD')
          : '-'}
      </div>
    ),
  },
  {
    dataField: 'end_date',
    text: t('gift_list.end_date'),
    headerClassname: 'w-[120px]',
    classname: 'w-[120px] text-gray-700',
    sort: true,
    formatter: (rowData) => (
      <div>
        {rowData.end_date
          ? dayjs(rowData.end_date.toString()).format('YYYY/MM/DD')
          : '-'}
      </div>
    ),
  },
  {
    dataField: 'limit',
    text: t('gift_list.limit'),
    headerClassname: 'w-[120px]',
    classname: 'w-[120px] text-gray-700',
    formatter: (rowData) => (
      <div>
        <span className="text-[#669F2A]">
          { rowData.redeemed_quantity}
        </span>
        /{rowData.total_quantity || t('gift_list.quantity_unlimited')}
      </div>
    ),
  },
  {
    dataField: 'exchange_point',
    text: t('gift_list.point_exchange'),
    headerClassname: 'w-[250px] max-w-[250px]',
    classname: 'w-[250px] text-gray-700 max-w-[250px]',
    formatter: (rowData) => {
      return (
        <div className="flex flex-col gap-2">
          <div>
            <span>{`${t('gift_list.participants')}: `}</span>
            {rowData.points_required_for_student?.toLocaleString() || '-'}
          </div>
          <div>
            <span>{`${t('gift_list.student_coach')}: `}</span>
            {rowData.points_required_for_teacher?.toLocaleString() || '-'}
          </div>
        </div>
      );
    },
  },
  {
    dataField: 'action',
    text: '',
    sort: false,
    classname: 'text-center [&>div]:justify-center',
    headerClassname: '[&>div]:justify-center',
    formatter: (rowData) => (
      <div className="flex flex-row h-[36px] items-center gap-8">
        <InputSwitch
          checked={rowData.status === giftStatus.PUBLIC ? true : false}
          onChange={(e) => onEditStatusGift?.(e.value, rowData)}
        />
        <Button className="p-[5px]" onClick={() => onEditGift?.(rowData.id)}>
          <span className="pi pi-pencil" />
        </Button>
        <Button className="p-[5px]" onClick={() => onDeleteGift?.(rowData)}>
          <span className="pi pi-trash text-error-500" />
        </Button>
      </div>
    ),
  },
];
